import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { fetchApi } from '../services/apiBase';
import { CollectionDetail } from '../services/plansApi';
import { BottomNav } from '../components/navigation';
import './CreatorProfile.css';

const fmt = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`;

// Mounted only through `creatorRoutes.tsx`, which reads `username` and `slug`
// off `/@<username>/collections/<slug>`.
const CollectionPage: React.FC<{ username: string; slug: string }> = ({ username, slug }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<CollectionDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!username || !slug) return;
    setLoading(true);
    fetchApi<CollectionDetail>(`/api/v1/plans/collections/by/${username}/${slug}/`)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [username, slug]);

  useEffect(() => { window.scrollTo(0, 0); }, [username, slug]);

  if (loading) {
    return (
      <div className="cp-page">
        <div className="cp-loading">Loading collection...</div>
        <BottomNav activeItem="explore" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="cp-page">
        <div className="cp-error">Collection not found</div>
        <BottomNav activeItem="explore" />
      </div>
    );
  }

  return (
    <div className="cp-page">
      <div className="cp-header">
        <button className="cp-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} />
        </button>
        <span className="cp-url-badge">vizday.com/@{data.creator_username}/collections/{data.slug}</span>
      </div>

      <div style={{ padding: '0 20px 20px' }}>
        <h1 className="cp-name" style={{ textAlign: 'left', marginBottom: 4 }}>{data.title}</h1>
        {data.description && (
          <p className="cp-bio" style={{ textAlign: 'left', marginBottom: 12 }}>{data.description}</p>
        )}
        <p className="cp-username" style={{ textAlign: 'left', marginBottom: 20 }}>
          by <span style={{ cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate(`/@${data.creator_username}`)}>{data.creator_name}</span> · {data.plan_count} plans
        </p>

        {data.plans.map(p => (
          <div key={p.id} className="cp-plan-card" onClick={() => navigate(`/plans/p/${p.slug}`)}>
            <div className="cp-plan-info">
              <span className="cp-plan-title">{p.title}</span>
              <span className="cp-plan-meta">
                {/* Joined from parts: at 0 uses the segment and its separator go together. */}
                {[
                  p.destination,
                  `${p.duration_days} days`,
                  p.used_count > 0 ? `${fmt(p.used_count)} used` : '',
                ].filter(Boolean).join(' · ')}
              </span>
            </div>
          </div>
        ))}
      </div>

      <BottomNav activeItem="explore" />
    </div>
  );
};

export default CollectionPage;
